import type {
  LocalDataContainer,
  LocalIndexRow,
  LocalIndexSegment,
} from '../../../local-data/payload';
import type { LocalAuthBinding } from '../../../storage/local-data-access';

/** Key material captured for one bounded segment read or write. */
export interface IndexSegmentKeys {
  open(
    keyScopeId: string,
    docId: string,
    kind: 'index',
    payload: LocalDataContainer
  ): Promise<Uint8Array>;
  seal(
    keyScopeId: string,
    docId: string,
    kind: 'index',
    plain: Uint8Array
  ): Promise<LocalDataContainer>;
  /** Retain only the authority token after a completed batch or rebuild. */
  releasePreparedKeys(): void;
}

export interface IndexSegmentHead {
  revision: string;
  segmentId: string;
}
export interface IndexSegmentSnapshot {
  state: IndexSegmentHead | null;
  count: number;
}
export interface IndexSegmentStart extends IndexSegmentSnapshot {
  meta: LocalIndexSegment | null;
}
export interface IndexSegmentRead<T, K extends IndexSegmentKeys> {
  value: T;
  context: K;
}
/** Expected stored revision of one segment; null means it must not exist. */
export interface IndexSegmentCAS {
  segmentId: string;
  expected: string | null;
}
export interface IndexSegmentChange {
  deletes: readonly IndexSegmentCAS[];
  puts: readonly { row: LocalIndexRow; expected: string | null }[];
  next: IndexSegmentHead;
}

/**
 * Durable sealed search-index segments of one account binding. Adapters own
 * only storage and key capture; every read is a consistent bounded snapshot and
 * every commit compares the index head and each touched segment atomically.
 */
export interface IndexSegmentStore<
  K extends IndexSegmentKeys = IndexSegmentKeys,
> {
  readonly binding: LocalAuthBinding;
  /** The account lock in memory: false once the connection locked. */
  readonly authorized: boolean;
  waitForConnected(signal: AbortSignal): Promise<void>;
  /** Calls `listener` once the backing connection closes or fails. */
  onClosed(listener: () => void): () => void;
  /** Head, segment count and head metadata row; `validate` runs before key capture. */
  readStart(
    validate: (start: IndexSegmentStart) => void,
    signal: AbortSignal
  ): Promise<IndexSegmentRead<IndexSegmentStart, K>>;
  /**
   * Table of every record row by nid under an unchanged head, read from the
   * record indexes only: no row is decrypted.
   */
  readRecordTables(
    expected: IndexSegmentHead | null,
    signal: AbortSignal
  ): Promise<Map<number, string>>;
  /**
   * Rows under an unchanged head with their keys captured: a number is the
   * nid of a record row, a string the segment id of a row.
   */
  readRows(
    expected: IndexSegmentHead | null,
    keys: readonly (number | string)[],
    signal: AbortSignal
  ): Promise<IndexSegmentRead<(LocalIndexRow | undefined)[], K>>;
  /** Every run part row under an unchanged head, with their keys captured. */
  readRunParts(
    expected: IndexSegmentHead | null,
    signal: AbortSignal
  ): Promise<IndexSegmentRead<LocalIndexRow[], K>>;
  /**
   * Record rows by [table, id] or nid under an unchanged head, without keys:
   * their routes and revisions, never their plaintext.
   */
  readRoutes(
    expected: IndexSegmentHead | null,
    keys: readonly ([string, string] | number)[],
    signal: AbortSignal
  ): Promise<(LocalIndexSegment | undefined)[]>;
  readSnapshot(signal?: AbortSignal): Promise<IndexSegmentSnapshot>;
  /** Removes every row and the head under an unchanged head: the index starts empty. */
  discard(
    expected: IndexSegmentHead | null,
    signal: AbortSignal
  ): Promise<void>;
  assertRead(context: K): Promise<void>;
  /**
   * assertRead and the head check in one transaction: LOCAL_CONFLICT when the
   * head moved away from `expected`.
   */
  assertSnapshot(context: K, expected: IndexSegmentHead | null): Promise<void>;
  /** Runs `work` again once when a concurrent key registration won. */
  write<T>(work: () => Promise<T>): Promise<T>;
  prepareWrite(
    expected: IndexSegmentHead | null,
    rows: readonly IndexSegmentCAS[],
    scopes: readonly string[],
    signal: AbortSignal
  ): Promise<K>;
  commitWrite(
    context: K,
    expected: IndexSegmentHead | null,
    change: IndexSegmentChange,
    signal: AbortSignal
  ): Promise<void>;
}

export function sameIndexHead(
  a: IndexSegmentHead | null | undefined,
  b: IndexSegmentHead | null | undefined
) {
  return (
    (a?.revision ?? null) === (b?.revision ?? null) &&
    (a?.segmentId ?? null) === (b?.segmentId ?? null)
  );
}
